// src/components/layout/Footer.jsx
import { NavLink, Link } from "react-router-dom";
import Container from "../ui/Container";
import { ShieldCheck, Phone, Mail, MapPin, ExternalLink, CreditCard, Lock } from "lucide-react";

export default function Footer() {
    const currentYear = new Date().getFullYear();
    
    return (
        <footer className="relative mt-20 bg-slate-950 text-slate-400 overflow-hidden pt-20 pb-10">
            {/* Background Decor */}
            <div className="pointer-events-none absolute bottom-0 left-0 h-64 w-64 rounded-full bg-brand-600/5 blur-[100px]" />
            <div className="pointer-events-none absolute top-0 right-0 h-64 w-64 rounded-full bg-blue-600/5 blur-[100px]" />

            <Container className="relative z-10">
                <div className="grid gap-12 lg:grid-cols-4 sm:grid-cols-2">
                    
                    {/* Brand Column */}
                    <div className="space-y-6">
                        <Link to="/" className="inline-flex items-center gap-3">
                            <img src="/images/logo.webp" alt="THB Logo" className="h-8 w-auto brightness-0 invert opacity-80" />
                            <span className="text-xl font-heading font-black text-white uppercase tracking-tight">Tuscher <span className="text-brand-600">Helmut</span></span>
                        </Link>
                        <p className="text-sm leading-relaxed max-w-xs">
                            Professionelle Betonrohrfallen und Zubehör für eine waidgerechte Raubwildbejagung. Seit über 40 Jahren Qualität aus der Praxis.
                        </p>
                        <div className="flex items-center gap-4">
                            <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-brand-600 transition-colors hover:bg-brand-600 hover:text-white">
                                <Phone className="h-5 w-5" />
                            </div>
                            <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-brand-600 transition-colors hover:bg-brand-600 hover:text-white">
                                <Mail className="h-5 w-5" />
                            </div>
                        </div>
                    </div>

                    {/* Quicklinks Column */}
                    <div>
                        <h4 className="text-xs font-black uppercase tracking-[0.2em] text-white mb-8">Navigation</h4>
                        <ul className="space-y-4">
                            {[{to: "/", label: "Startseite"}, {to: "/shop", label: "Shop"}, {to: "/ueber", label: "Über"}, {to: "/aufbau", label: "Aufbau-Anleitung"}, {to: "/blog", label: "Blog"}].map((l) => (
                                <li key={l.to}>
                                    <NavLink to={l.to} className="text-sm font-bold hover:text-brand-600 transition-colors flex items-center gap-2 group">
                                        <div className="h-1 w-1 rounded-full bg-brand-600 opacity-0 group-hover:opacity-100 transition-all" />
                                        {l.label}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Support & Legal Column */}
                    <div>
                        <h4 className="text-xs font-black uppercase tracking-[0.2em] text-white mb-8">Rechtliches</h4>
                        <ul className="space-y-4">
                            {[{to: "/impressum", label: "Impressum"}, {to: "/datenschutz", label: "Datenschutz"}, {to: "/versand", label: "Versand & Zahlung"}, {to: "/kontakt", label: "Kontakt"}].map((l) => (
                                <li key={l.to}>
                                    <NavLink to={l.to} className="text-sm font-bold hover:text-brand-600 transition-colors flex items-center gap-2 group">
                                        <div className="h-1 w-1 rounded-full bg-slate-700 opacity-0 group-hover:opacity-100 transition-all" />
                                        {l.label}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Specialized Info Column */}
                    <div className="space-y-8">
                        <div>
                            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-white mb-6">Sicheres Bezahlen</h4>
                            <div className="flex flex-wrap gap-3">
                                <div className="rounded-xl bg-white/5 border border-white/10 p-2.5 flex items-center gap-3">
                                    <CreditCard className="h-4 w-4 text-slate-500" />
                                    <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest leading-none">PayPal & Vorkasse</span>
                                </div>
                                <div className="rounded-xl bg-white/5 border border-white/10 p-2.5 flex items-center gap-3">
                                    <Lock className="h-4 w-4 text-emerald-600" />
                                    <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest leading-none">SSL Verschlüsselt</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-5 rounded-2xl bg-brand-600/10 border border-brand-600/20">
                            <div className="flex items-center gap-3 text-brand-600 mb-2">
                                <ShieldCheck className="h-5 w-5" />
                                <span className="text-[10px] font-black uppercase tracking-widest">Wichtiger Hinweis</span>
                            </div>
                            <p className="text-[10px] italic leading-relaxed text-slate-400">
                                Gemäß § 19 UStG wird keine Umsatzsteuer berechnet. Ich liefere per DHL und Spedition innerhalb von DE und AT.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-20 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-600">
                        &copy; {currentYear} Helmut Tuscher – Betonrohrfallen
                    </div>
                    <div className="flex items-center gap-8">
                        <span className="flex items-center gap-2 text-[10px] font-bold text-slate-500 hover:text-white transition-colors cursor-help">
                            <MapPin className="h-3 w-3" /> Bayern, Deutschland
                        </span>
                    </div>
                </div>
            </Container>
        </footer>
    );
}